import { motion, AnimatePresence } from 'framer-motion';
import clsx from 'clsx';
import { ChevronLeft, ChevronRight, TrendingUp, Zap } from 'lucide-react';
import { useAppStore } from '../store/useAppStore';
import { useOSStore } from '../store/useOSStore';
import { useNavigate } from 'react-router-dom';

// ── Module rail config ────────────────────────────────────────────────────────

interface RailModule {
  id: string;
  emoji: string;
  label: string;
  tag: string;
  leagueId: string;
  leagueIds?: string[];
  to: string;
  accentColor: string;
  available: boolean;
}

const RAIL_MODULES: RailModule[] = [
  { id: 'diamondedge', emoji: '⚾', label: 'DiamondEdge', tag: 'MLB',        leagueId: 'mlb',  to: '/diamondedge',  accentColor: 'text-brand',         available: true  },
  { id: 'courtedge',   emoji: '🏀', label: 'CourtEdge',   tag: 'WNBA · NBA', leagueId: 'wnba', leagueIds: ['wnba', 'nba'], to: '/courtedge', accentColor: 'text-warn', available: true },
  { id: 'griironedge', emoji: '🏈', label: 'GridironEdge', tag: 'NFL',       leagueId: 'nfl',  to: '/griironedge',  accentColor: 'text-loss',          available: false },
  { id: 'iceedge',     emoji: '🏒', label: 'IceEdge',      tag: 'NHL',       leagueId: 'nhl',  to: '/iceedge',      accentColor: 'text-info',          available: false },
  { id: 'pitchedge',   emoji: '⚽', label: 'PitchEdge',    tag: 'SOC',       leagueId: 'mls',  to: '/pitchedge',    accentColor: 'text-win',           available: false },
  { id: 'octagonedge', emoji: '🥊', label: 'OctagonEdge',  tag: 'MMA',       leagueId: 'ufc',  to: '/octagonedge',  accentColor: 'text-purple-bright', available: false },
];

// ── Module summary card ───────────────────────────────────────────────────────

function ModuleCard({ mod }: { mod: RailModule }) {
  const navigate = useNavigate();
  const { recommendations, agentActivity } = useAppStore();

  const leagues = mod.leagueIds ?? [mod.leagueId];
  const recs = recommendations.filter((r) => leagues.includes(r.leagueId ?? ''));
  const strongCount = recs.filter((r) => r.decisionStatus === 'STRONG_BET').length;
  const qualifiedCount = recs.filter((r) => r.decisionStatus === 'QUALIFIED_BET').length;
  const totalActive = strongCount + qualifiedCount;
  const hasActivity = agentActivity.some((e) => e.gamePk && recs.some((r) => String(r.gamePk) === String(e.gamePk)));

  return (
    <button
      disabled={!mod.available}
      onClick={() => mod.available && void navigate(mod.to)}
      className={clsx(
        'w-full text-left px-3 py-2.5 rounded-lg border transition-all duration-150 group',
        mod.available
          ? 'border-surface-3 hover:border-surface-4 hover:bg-surface-2 cursor-pointer'
          : 'border-surface-2 opacity-40 cursor-not-allowed',
      )}
    >
      <div className="flex items-center justify-between mb-1.5">
        <div className="flex items-center gap-1.5">
          <span className="text-sm leading-none">{mod.emoji}</span>
          <div>
            <p className={clsx('text-[10px] font-bold leading-tight', mod.available ? mod.accentColor : 'text-gray-500')}>
              {mod.label}
            </p>
            <p className="text-[8px] text-gray-600 uppercase tracking-widest leading-tight">{mod.tag}</p>
          </div>
        </div>
        {hasActivity && mod.available && (
          <span className="relative flex h-1.5 w-1.5 flex-shrink-0">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-purple opacity-60" />
            <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-purple" />
          </span>
        )}
        {!mod.available && (
          <span className="text-[8px] text-gray-700 uppercase tracking-wider font-semibold">soon</span>
        )}
      </div>

      {mod.available && (
        <div className="flex items-center gap-2">
          {strongCount > 0 && (
            <div className="flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-gold flex-shrink-0" />
              <span className="text-[9px] font-mono font-semibold text-gold">{strongCount} BET_NOW</span>
            </div>
          )}
          {qualifiedCount > 0 && (
            <div className="flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-brand flex-shrink-0" />
              <span className="text-[9px] font-mono text-gray-400">{qualifiedCount} QUAL</span>
            </div>
          )}
          {totalActive === 0 && (
            <span className="text-[9px] text-gray-700 italic">no active picks</span>
          )}
        </div>
      )}
    </button>
  );
}

// ── Collapsed strip ───────────────────────────────────────────────────────────

function CollapsedStrip({ onExpand }: { onExpand: () => void }) {
  const { recommendations } = useAppStore();
  const totalStrong = recommendations.filter((r) => r.decisionStatus === 'STRONG_BET').length;

  return (
    <div className="flex flex-col items-center justify-between h-full w-10 bg-surface-1 border-r border-surface-4 py-2">
      <button
        onClick={onExpand}
        title="Expand Intelligence Rail"
        className="flex items-center justify-center w-8 h-8 rounded-lg hover:bg-surface-2 text-gray-500 hover:text-gray-200 transition-colors"
      >
        <ChevronRight className="w-3.5 h-3.5" />
      </button>
      <div className="flex-1 flex flex-col items-center justify-center gap-2">
        {totalStrong > 0 && (
          <span className="flex items-center justify-center w-5 h-5 rounded-full bg-gold/15 border border-gold/30">
            <span className="text-[8px] font-bold text-gold tabular-nums leading-none">{Math.min(totalStrong, 9)}</span>
          </span>
        )}
        <p className="text-[8px] font-bold text-gray-700 uppercase tracking-widest [writing-mode:vertical-rl] select-none">
          Modules
        </p>
      </div>
      <div className="w-8 h-8" />
    </div>
  );
}

// ── IntelligenceRail ──────────────────────────────────────────────────────────

export function IntelligenceRail() {
  const { intelligenceRailOpen, toggleIntelligenceRail, focusMode } = useOSStore();
  const { recommendations } = useAppStore();

  const betNowTotal = recommendations.filter((r) => r.decisionStatus === 'STRONG_BET').length;
  const qualTotal   = recommendations.filter((r) => r.decisionStatus === 'QUALIFIED_BET').length;

  if (!intelligenceRailOpen) {
    return <CollapsedStrip onExpand={toggleIntelligenceRail} />;
  }

  return (
    <AnimatePresence>
      <motion.div
        key="intel-rail"
        initial={{ width: 40, opacity: 0 }}
        animate={{ width: 240, opacity: 1 }}
        exit={{ width: 40, opacity: 0 }}
        transition={{ duration: 0.2, ease: [0.32, 0.72, 0, 1] }}
        className={clsx(
          'flex flex-col h-full bg-surface-1 border-r border-surface-4 flex-shrink-0 overflow-hidden transition-opacity duration-300',
          focusMode && 'opacity-20 hover:opacity-100',
        )}
        style={{ width: 240 }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-3 py-2 border-b border-surface-4 flex-shrink-0">
          <div className="flex items-center gap-2">
            <div className="flex items-center justify-center w-6 h-6 rounded-md bg-brand/10 border border-brand/20">
              <TrendingUp className="w-3 h-3 text-brand" />
            </div>
            <div>
              <p className="text-[11px] font-bold text-gray-100 leading-tight">Intelligence</p>
              <p className="text-[9px] text-brand/60 uppercase tracking-widest leading-tight">Module Rail</p>
            </div>
          </div>
          <button
            onClick={toggleIntelligenceRail}
            className="p-1 text-gray-600 hover:text-gray-300 hover:bg-surface-2 rounded transition-colors"
            title="Collapse rail"
          >
            <ChevronLeft className="w-3.5 h-3.5" />
          </button>
        </div>

        {/* Summary strip */}
        <div className="flex items-center gap-3 px-3 py-2 border-b border-surface-4 flex-shrink-0">
          {betNowTotal > 0 ? (
            <div className="flex items-center gap-1">
              <Zap className="w-2.5 h-2.5 text-gold" />
              <span className="text-[9px] font-mono font-semibold text-gold">{betNowTotal} BET_NOW</span>
            </div>
          ) : null}
          {qualTotal > 0 ? (
            <div className="flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-brand" />
              <span className="text-[9px] font-mono text-gray-400">{qualTotal} QUAL</span>
            </div>
          ) : null}
          {betNowTotal === 0 && qualTotal === 0 && (
            <span className="text-[9px] text-gray-600 italic">no live picks</span>
          )}
        </div>

        {/* Module cards */}
        <div className="flex-1 overflow-y-auto p-2 space-y-1.5">
          {RAIL_MODULES.map((mod) => (
            <ModuleCard key={mod.id} mod={mod} />
          ))}
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
